import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChangeEvent, FormEvent } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { updateMyProfile } from '../../api/authApi'
import type { ProfileFormState, UpdateProfilePayload } from '../../types/auth'
import type { StatusMessage } from '../../types/status'
import { validateConfirmedPassword } from '../../utils/authFormValidation'
import { createProfileFormState, resolveApiErrorMessage } from './shared'

interface UseAuthProfileFormParams {
  apiBaseUrl: string
  navigate: NavigateFunction
  isAuthenticated: boolean
  authUser: string
  authEmail: string
  requirePasswordChange: boolean
  clearSessionState: () => void
  setAuthStatus: (status: StatusMessage | null) => void
  setAuthUser: (value: string) => void
  setAuthEmail: (value: string) => void
  setRequirePasswordChange: (value: boolean) => void
}

export interface UseAuthProfileFormResult {
  profileForm: ProfileFormState
  profileStatus: StatusMessage | null
  isProfileSubmitting: boolean
  setProfileStatus: (status: StatusMessage | null) => void
  clearProfileState: () => void
  resetProfileForm: (userName?: string, userEmail?: string) => void
  openProfilePage: () => void
  handleProfileFormChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleProfileSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>
}

export function useAuthProfileForm({
  apiBaseUrl,
  navigate,
  isAuthenticated,
  authUser,
  authEmail,
  requirePasswordChange,
  clearSessionState,
  setAuthStatus,
  setAuthUser,
  setAuthEmail,
  setRequirePasswordChange
}: UseAuthProfileFormParams): UseAuthProfileFormResult {
  const [isProfileSubmitting, setIsProfileSubmitting] = useState(false)
  const [profileStatus, setProfileStatus] = useState<StatusMessage | null>(null)
  const [profileForm, setProfileForm] = useState<ProfileFormState>(() => createProfileFormState())
  const profileDefaultsRef = useRef({
    authUser,
    authEmail
  })

  useEffect(() => {
    profileDefaultsRef.current = {
      authUser,
      authEmail
    }
  }, [authEmail, authUser])

  const resetProfileForm = useCallback((userName?: string, userEmail?: string): void => {
    const defaults = profileDefaultsRef.current
    setProfileForm(createProfileFormState(userName ?? defaults.authUser, userEmail ?? defaults.authEmail))
  }, [])

  const clearProfileState = useCallback((): void => {
    setIsProfileSubmitting(false)
    setProfileStatus(null)
  }, [])

  const openProfilePage = useCallback((): void => {
    if (isProfileSubmitting) return
    setProfileStatus(null)
    resetProfileForm()
    navigate('/profile')
  }, [isProfileSubmitting, navigate, resetProfileForm])

  const handleProfileFormChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = event.target
    setProfileForm((prev) => ({
      ...prev,
      [name]: value
    }))
  }, [])

  const handleProfileSubmit = useCallback(async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (!isAuthenticated) return

    const currentDisplayName = authUser.trim()
    const currentEmail = authEmail.trim().toLowerCase()
    const normalizedDisplayName = profileForm.displayName.trim()
    const normalizedEmail = profileForm.email.trim().toLowerCase()
    const normalizedCurrentPassword = profileForm.currentPassword
    const normalizedNewPassword = profileForm.newPassword
    const normalizedConfirmPassword = profileForm.confirmNewPassword
    const shouldUpdateDisplayName = normalizedDisplayName.length > 0 && normalizedDisplayName !== currentDisplayName
    const shouldUpdateEmail = normalizedEmail.length > 0 && normalizedEmail !== currentEmail
    const shouldUpdatePassword = normalizedNewPassword.length > 0

    if (requirePasswordChange && !shouldUpdatePassword) {
      setProfileStatus({ kind: 'error', message: 'При первом входе необходимо указать новый пароль.' })
      return
    }

    if (shouldUpdatePassword && !normalizedCurrentPassword) {
      setProfileStatus({ kind: 'error', message: 'Для смены пароля укажите текущий пароль.' })
      return
    }

    if (shouldUpdatePassword) {
      const passwordValidationError = validateConfirmedPassword(
        normalizedNewPassword,
        normalizedConfirmPassword,
        { mismatchMessage: 'Новый пароль и подтверждение не совпадают.' }
      )
      if (passwordValidationError) {
        setProfileStatus({ kind: 'error', message: passwordValidationError })
        return
      }
    }

    if (!shouldUpdateDisplayName && !shouldUpdateEmail && !shouldUpdatePassword) {
      setProfileStatus({ kind: 'error', message: 'Нет изменений для сохранения.' })
      return
    }

    setIsProfileSubmitting(true)
    setProfileStatus({ kind: 'loading', message: 'Сохранение профиля...' })

    try {
      const updatePayload: UpdateProfilePayload = {
        displayName: shouldUpdateDisplayName ? normalizedDisplayName : null,
        email: shouldUpdateEmail ? normalizedEmail : null,
        currentPassword: shouldUpdatePassword || requirePasswordChange ? normalizedCurrentPassword : null,
        newPassword: shouldUpdatePassword ? normalizedNewPassword : null
      }
      const payload = await updateMyProfile(apiBaseUrl, updatePayload)

      if (payload?.requireRelogin) {
        clearSessionState()
        clearProfileState()
        setAuthStatus({
          kind: 'success',
          message: payload?.message || 'Профиль обновлен. Выполните вход снова.'
        })
        navigate('/login', { replace: true })
        return
      }

      if (shouldUpdateDisplayName) {
        setAuthUser(normalizedDisplayName)
      }

      if (shouldUpdateEmail) {
        setAuthEmail(normalizedEmail)
      }

      const mustChangePassword = Boolean(payload?.requirePasswordChange)
      setRequirePasswordChange(mustChangePassword)
      if (!mustChangePassword) {
        navigate('/')
      }
      setProfileStatus({ kind: 'success', message: payload?.message || 'Профиль обновлен.' })
    } catch (error) {
      setProfileStatus({
        kind: 'error',
        message: resolveApiErrorMessage(error, apiBaseUrl, 'Не удалось обновить профиль.')
      })
    } finally {
      setIsProfileSubmitting(false)
    }
  }, [
    apiBaseUrl,
    authEmail,
    authUser,
    clearProfileState,
    clearSessionState,
    isAuthenticated,
    navigate,
    profileForm,
    requirePasswordChange,
    setAuthEmail,
    setAuthStatus,
    setAuthUser,
    setRequirePasswordChange
  ])

  return {
    profileForm,
    profileStatus,
    isProfileSubmitting,
    setProfileStatus,
    clearProfileState,
    resetProfileForm,
    openProfilePage,
    handleProfileFormChange,
    handleProfileSubmit
  }
}
